import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Check,
  Clock,
  Copy,
  Globe,
  Image as ImageIcon,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Plus,
} from "lucide-react";
import {
  followUser,
  getOtherUserProfile,
  getUserProfile,
  resetOtherUserProfile,
  unfollowUser,
} from "reducer/profileSlice";
import { getAllPosts } from "reducer/postsSlice";
import { sentEmailRequest } from "reducer/requestEmailSlice";
import { serverFiles, formatPersonName, splitProfileIndustries, formatLastSeen } from "utils/utils";
import toast from "components/Toast/toast";
import FeedPostCard from "components/feed/FeedPostCard";
import EditProfileModal from "components/app/EditProfileModal";
import FollowConnectionsModal from "components/app/FollowConnectionsModal";
import PresenceDot from "components/feed/PresenceDot";
import NO_IMAGE from "images/NO_IMAGE.jpg";
import useResolvedLocation from "hooks/useResolvedLocation";

const TABS = ["portfolio", "posts", "about"];

const Avatar = ({ src, name, className }) =>
  src ? (
    <img
      src={src}
      alt={name}
      className={`${className} object-cover`}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = NO_IMAGE;
      }}
    />
  ) : (
    <div
      className={`${className} avatar-fallback flex items-center justify-center font-display font-bold text-3xl`}
    >
      {(name || "Q")
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()}
    </div>
  );

const UserProfilePage = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const followBtnRef = useRef(0);
  const requestEmailRef = useRef(0);

  const { userPosts, isPostsLoading } = useSelector((state) => state.posts);
  const { userProfile, otherUserProfile, isProfileLoading } = useSelector(
    (state) => state.userProfile
  );

  const [tab, setTab] = useState("portfolio");
  const [editOpen, setEditOpen] = useState(false);
  const [followModal, setFollowModal] = useState(false);
  const [modalType, setModalType] = useState("FOLLOWERS");
  const [followBusy, setFollowBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);

  const locationStr = useResolvedLocation(id ? otherUserProfile : userProfile);

  const currentUserId =
    userProfile?.userAliasId ||
    userProfile?.companyResponseDto?.companyAliasId;

  const profileData = id ? otherUserProfile : userProfile;
  const otherUserId =
    otherUserProfile?.userAliasId ||
    otherUserProfile?.companyResponseDto?.companyAliasId;

  const isSelf = !id || otherUserId === currentUserId;
  const isBrand = profileData?.userType === "BUSINESS";
  const displayName = isBrand
    ? profileData?.companyResponseDto?.name || "Brand"
    : formatPersonName(profileData?.firstName, profileData?.lastName) ||
      "User";

  const avatarSrc = profileData?.profileUrl
    ? serverFiles(profileData.profileUrl)
    : profileData?.companyResponseDto?.logo
      ? serverFiles(profileData.companyResponseDto.logo)
      : null;

  // roles = creative disciplines (skills); industries = industry list (incl. custom "Other")
  const rawIndustry = (
    isBrand
      ? profileData?.companyResponseDto?.industry ||
        profileData?.industry ||
        []
      : profileData?.industry || []
  ).filter(Boolean);
  const rawSkills = isBrand
    ? []
    : Array.isArray(profileData?.skills)
      ? profileData.skills.filter(Boolean)
      : [];
  const { industries, roles } = splitProfileIndustries(rawIndustry, rawSkills, {
    isBrand,
  });
  // Avoid showing the same label twice if skills and industry overlap
  const industryChips = industries.filter(
    (i) => !roles.some((r) => String(r).toLowerCase() === String(i).toLowerCase())
  );
  const roleLine =
    roles.slice(0, 2).join(" · ") ||
    industries.slice(0, 2).join(" · ") ||
    (isBrand ? "Brand" : "Creative");
  const bio =
    profileData?.bio ||
    profileData?.about ||
    profileData?.companyResponseDto?.about ||
    "";
  const website = profileData?.companyResponseDto?.websiteUrl || "";
  const emailStatus = profileData?.status; // null | PENDING | ACCEPT | REJECT
  const following = !!profileData?.isFollowed;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    dispatch(resetOtherUserProfile());
    setFollowModal(false);
    setTab("portfolio");
  }, [pathname, dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(getOtherUserProfile(id));
    } else {
      dispatch(getUserProfile());
    }
  }, [id, dispatch]);

  useEffect(() => {
    const ownerId = id ? otherUserId : currentUserId;
    if (ownerId) {
      dispatch(getAllPosts({ postOwnerId: ownerId, pageNo: 1, perPage: 50 }));
    }
  }, [id, otherUserId, currentUserId, dispatch]);

  const posts = Array.isArray(userPosts) ? userPosts : [];
  const portfolio = useMemo(
    () =>
      posts.filter((p) =>
        (p?.medias || []).some((m) => {
          const type = String(m?.mediaType || m?.type || "").toUpperCase();
          const path = m?.path || "";
          return (
            type.includes("IMAGE") ||
            type.includes("PHOTO") ||
            /\.(jpe?g|png|gif|webp)(\?|$)/i.test(path)
          );
        })
      ),
    [posts]
  );

  const toggleFollow = async () => {
    const now = Date.now();
    if (now - followBtnRef.current < 800 || followBusy) return;
    followBtnRef.current = now;
    setFollowBusy(true);
    const payload = { userAliasId: otherUserId };
    try {
      if (following) {
        await dispatch(unfollowUser({ payload, postId: null }));
      } else {
        await dispatch(followUser({ payload, postId: null }));
      }
      if (id) dispatch(getOtherUserProfile(id));
    } catch (_) {
      toast.error("Could not update follow");
    } finally {
      setFollowBusy(false);
    }
  };

  const requestEmail = async () => {
    const now = Date.now();
    if (now - requestEmailRef.current < 1500 || emailBusy) return;
    requestEmailRef.current = now;
    setEmailBusy(true);
    try {
      await dispatch(sentEmailRequest(profileData?.userAliasId));
      if (id) dispatch(getOtherUserProfile(id));
    } catch (error) {
      toast.error(error?.message || "Could not request email");
    } finally {
      setEmailBusy(false);
    }
  };

  const acceptedEmail = String(profileData?.email || "").trim();
  const EMAIL_SUBJECT = "I'd love to connect";
  const EMAIL_BODY = "Hi,\n\nI'd love to connect.\n";
  const mailtoHref = acceptedEmail
    ? `mailto:${acceptedEmail}?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(EMAIL_BODY)}`
    : "#";

  const openAcceptedEmailCompose = async (event) => {
    if (!acceptedEmail) {
      event.preventDefault();
      return;
    }
    try {
      await navigator.clipboard.writeText(acceptedEmail);
      toast.success("Email copied");
    } catch {
      // Clipboard may be blocked; the address is still on the profile.
    }

    const ua = navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    if (!isAndroid) {
      // iOS/desktop: use the native mailto link so To + subject are filled.
      return;
    }

    event.preventDefault();
    const to = encodeURIComponent(acceptedEmail);
    const subject = encodeURIComponent(EMAIL_SUBJECT);
    const body = encodeURIComponent(EMAIL_BODY);
    const fallback = encodeURIComponent(mailtoHref);
    window.location.href =
      `intent://send?to=${to}&subject=${subject}&body=${body}` +
      `#Intent;scheme=mailto;package=com.google.android.gm;S.browser_fallback_url=${fallback};end`;
  };

  const copyAcceptedEmail = async () => {
    if (!acceptedEmail) return;
    try {
      await navigator.clipboard.writeText(acceptedEmail);
      toast.success("Email copied");
    } catch {
      toast.error("Could not copy email");
    }
  };

  const loading =
    (isProfileLoading && !profileData) ||
    (id && !otherUserProfile && isPostsLoading);

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pb-16">
      {/* Cover / banner */}
      <div className="relative h-44 md:h-60 rounded-b-3xl overflow-hidden">
        <div className="absolute inset-0 bg-zinc-900" />
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 30% 40%, rgba(108,99,245,0.45), transparent 60%)",
          }}
        />
      </div>

      <div className="px-1 sm:px-4">
        <div className="flex items-start justify-between gap-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="-mt-14 md:-mt-20 shrink-0 relative z-10"
          >
            <div className="relative inline-block">
              <Avatar
                src={avatarSrc}
                name={displayName}
                className={`h-28 w-28 md:h-36 md:w-36 border-4 border-zinc-950 shadow-xl ${
                  isBrand ? "rounded-3xl" : "rounded-3xl"
                }`}
              />
              <PresenceDot
                online={!!profileData?.isOnline}
                size={16}
                className="absolute bottom-1.5 right-1.5 border-[3px] border-zinc-950"
              />
            </div>
          </motion.div>

          {isSelf ? (
            <div className="flex items-center gap-3 pt-4">
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-[#6C63F5] text-white hover:bg-[#5751E5] px-5 py-2.5 text-sm font-semibold transition-colors"
              >
                <Pencil size={15} /> Edit profile
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 pt-4 flex-nowrap justify-end min-w-0">
              <button
                type="button"
                onClick={toggleFollow}
                disabled={followBusy}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 sm:px-5 py-2 sm:py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 shrink-0 ${
                  following
                    ? "border border-zinc-700 text-zinc-200 hover:border-zinc-500"
                    : "bg-[#6C63F5] text-white hover:bg-[#5751E5]"
                }`}
              >
                {following ? (
                  <>
                    <Check size={16} /> Following
                  </>
                ) : (
                  <>
                    <Plus size={16} /> Follow
                  </>
                )}
              </button>
              {!isBrand && emailStatus === "ACCEPT" && acceptedEmail ? (
                <div className="inline-flex items-center gap-1.5 shrink-0">
                  <a
                    href={mailtoHref}
                    onClick={openAcceptedEmailCompose}
                    className="inline-flex items-center justify-center h-9 w-9 sm:hidden rounded-full border border-[#6C63F5]/40 bg-[#6C63F5]/10 text-[#c9c5ff]"
                    title={`Email ${acceptedEmail}`}
                    aria-label={`Email ${acceptedEmail}`}
                  >
                    <Mail size={16} />
                  </a>
                  <a
                    href={mailtoHref}
                    onClick={openAcceptedEmailCompose}
                    className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#6C63F5]/40 bg-[#6C63F5]/10 text-[#c9c5ff] px-5 py-2.5 text-sm font-medium hover:border-[#6C63F5]/70 max-w-[min(100%,280px)]"
                    title={`Send email to ${acceptedEmail}`}
                  >
                    <Mail size={16} className="shrink-0" />
                    <span className="truncate">{acceptedEmail}</span>
                  </a>
                  <button
                    type="button"
                    onClick={copyAcceptedEmail}
                    className="hidden sm:inline-flex items-center justify-center h-10 w-10 rounded-full border border-zinc-700 text-zinc-200 hover:border-zinc-500 hover:text-white"
                    title="Copy email"
                    aria-label="Copy email"
                  >
                    <Copy size={15} />
                  </button>
                </div>
              ) : !isBrand ? (
                <button
                  type="button"
                  onClick={requestEmail}
                  disabled={
                    emailBusy ||
                    emailStatus === "PENDING" ||
                    emailStatus === "REJECT"
                  }
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                    emailStatus === "PENDING"
                      ? "border border-amber-500/40 bg-amber-500/10 text-amber-300 cursor-not-allowed"
                      : emailStatus === "REJECT"
                        ? "border border-zinc-800 text-zinc-500 cursor-not-allowed"
                        : "border border-zinc-700 text-zinc-200 hover:border-zinc-500"
                  }`}
                >
                  {emailBusy ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span className="hidden sm:inline">Sending…</span>
                    </>
                  ) : emailStatus === "PENDING" ? (
                    <>
                      <Clock size={16} />
                      <span className="hidden sm:inline">Requested</span>
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      <span className="hidden sm:inline">Request email</span>
                    </>
                  )}
                </button>
              ) : null}
            </div>
          )}
        </div>

        <div className="mt-5">
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-zinc-50">
            {displayName}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-400">
            <span className="text-[#8b83ff] font-medium">{roleLine}</span>
            {locationStr ? (
              <span className="inline-flex items-center gap-1">
                <MapPin size={14} /> {locationStr}
              </span>
            ) : null}
            <span className="capitalize rounded-full border border-zinc-800 px-2 py-0.5 text-xs">
              {isBrand ? "brand" : "creative"}
            </span>
            {profileData?.isOnline ? (
              <span className="text-emerald-400 font-medium">Online</span>
            ) : formatLastSeen(profileData?.lastSeenAt) ? (
              <span>{formatLastSeen(profileData.lastSeenAt)}</span>
            ) : null}
          </div>
          {!isSelf && !isBrand && emailStatus === "ACCEPT" && acceptedEmail ? (
            <div className="mt-3 sm:hidden flex items-center gap-2 min-w-0">
              <a
                href={mailtoHref}
                className="text-sm text-[#c9c5ff] underline underline-offset-2 truncate"
                title={acceptedEmail}
              >
                {acceptedEmail}
              </a>
              <button
                type="button"
                onClick={copyAcceptedEmail}
                className="inline-flex items-center justify-center h-8 w-8 shrink-0 rounded-full border border-zinc-700 text-zinc-200"
                title="Copy email"
                aria-label="Copy email"
              >
                <Copy size={14} />
              </button>
            </div>
          ) : null}
        </div>

        {bio && (
          <p className="mt-6 max-w-2xl text-zinc-300 leading-relaxed">{bio}</p>
        )}

        {(roles.length > 0 || industryChips.length > 0) && (
          <div className="mt-4 flex flex-wrap gap-2">
            {roles.map((r) => (
              <span
                key={`role-${r}`}
                className="rounded-full border border-[#6C63F5]/30 bg-[#6C63F5]/10 text-[#c9c5ff] px-3 py-1 text-xs"
              >
                {r}
              </span>
            ))}
            {industryChips.map((r) => (
              <span
                key={`industry-${r}`}
                className="rounded-full border border-zinc-800 bg-zinc-900/60 px-3 py-1 text-xs text-zinc-300"
              >
                {r}
              </span>
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 sm:gap-x-8 border-y border-zinc-800/80 py-4">
          {[
            [String(profileData?.followerCount || 0), "Followers", "FOLLOWERS"],
            [
              String(profileData?.followingCount || 0),
              "Following",
              "FOLLOWING",
            ],
            [String(posts.length), "Posts", null],
          ].map(([n, l, type]) => (
            <button
              key={l}
              type="button"
              onClick={() => {
                if (!type) return;
                setModalType(type);
                setFollowModal(true);
              }}
              className={`text-left ${type ? "hover:opacity-80" : "cursor-default"}`}
            >
              <span className="font-display font-semibold text-xl text-zinc-50">
                {n}
              </span>
              <span className="ml-2 text-sm text-zinc-500">{l}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-1">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`relative rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${
                tab === t
                  ? "text-zinc-50"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {t}
              {tab === t && (
                <motion.span
                  layoutId="profile-tab"
                  className="absolute inset-0 -z-10 rounded-full bg-zinc-800/80"
                />
              )}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="py-16 text-center text-zinc-500 flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          ) : tab === "portfolio" ? (
            portfolio.length ? (
              <div className="columns-2 md:columns-3 gap-4">
                {portfolio.map((p, i) => {
                  const media = (p?.medias || []).find((m) => {
                    const type = String(
                      m?.mediaType || m?.type || ""
                    ).toUpperCase();
                    const path = m?.path || "";
                    return (
                      type.includes("IMAGE") ||
                      type.includes("PHOTO") ||
                      /\.(jpe?g|png|gif|webp)(\?|$)/i.test(path)
                    );
                  });
                  const src = media?.path ? serverFiles(media.path) : null;
                  if (!src) return null;
                  return (
                    <motion.div
                      key={p.id || i}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
                      className="mb-4 break-inside-avoid rounded-2xl overflow-hidden border border-zinc-800 group"
                    >
                      <img
                        src={src}
                        alt=""
                        loading="lazy"
                        className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-zinc-800 py-16 text-center text-zinc-500">
                <ImageIcon size={22} className="mx-auto mb-3 text-zinc-600" />
                No work yet — share a post with an image and it lands here.
              </div>
            )
          ) : tab === "posts" ? (
            posts.length ? (
              <div className="space-y-4">
                {posts.map((p) => (
                  <FeedPostCard key={p.id} post={p} asBriefCard={false} />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-zinc-800 py-16 text-center text-zinc-500">
                No posts yet.
              </div>
            )
          ) : (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 max-w-2xl">
              <h3 className="font-display font-semibold text-zinc-50 text-lg mb-3">
                About
              </h3>
              <p className="text-zinc-300 leading-relaxed">
                {bio || "No bio added yet."}
              </p>
              <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-zinc-500 mb-1">Account</div>
                  <div className="text-zinc-200 capitalize">
                    {isBrand ? "brand" : "creative"}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-500 mb-1">Based in</div>
                  <div className="text-zinc-200">{locationStr || "—"}</div>
                </div>
                {!isBrand && (
                  <div>
                    <div className="text-zinc-500 mb-1">Creatives</div>
                    <div className="text-zinc-200">
                      {roles.join(", ") || "—"}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-zinc-500 mb-1">Industries</div>
                  <div className="text-zinc-200">
                    {industries.join(", ") || "—"}
                  </div>
                </div>
                {website && (
                  <div className="col-span-2">
                    <div className="text-zinc-500 mb-1">Website</div>
                    <div className="text-zinc-200 inline-flex items-center gap-1.5">
                      <Globe size={13} /> {website}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <EditProfileModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />

      <FollowConnectionsModal
        open={followModal}
        onClose={() => setFollowModal(false)}
        initialType={modalType}
        followerCount={profileData?.followerCount}
        followingCount={profileData?.followingCount}
        userId={
          profileData?.userAliasId ||
          profileData?.companyResponseDto?.companyAliasId
        }
      />
    </div>
  );
};

export default UserProfilePage;
